import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import {
    Download,
    Loader2,
    CheckCircle2,
    FileText,
    Banknote,
} from "lucide-react";
import { toast } from "sonner";
import { useClientAuth } from "@/lib/clientAuth";
import PortalShell from "@/components/PortalShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
    sent: "border-ember/40 text-ember",
    paid: "border-emerald-400/40 text-emerald-400",
    overdue: "border-red-400/40 text-red-400",
    cancelled: "border-white/10 text-bone-500",
    draft: "border-white/10 text-bone-500",
};

export default function ClientInvoices() {
    const { client, token, loading, logout } = useClientAuth();
    const [invoices, setInvoices] = useState([]);
    const [busy, setBusy] = useState(true);
    const [downloading, setDownloading] = useState(null);

    const headers = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token],
    );

    const refresh = () =>
        axios
            .get(`${API}/client/invoices`, { headers })
            .then((r) => setInvoices(r.data))
            .catch((err) => {
                if (err?.response?.status === 401) logout();
                else toast.error("Could not load invoices");
            });

    useEffect(() => {
        if (!token) return;
        setBusy(true);
        refresh().finally(() => setBusy(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    if (loading)
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-ember" />
            </div>
        );
    if (!client) return <Navigate to="/portal/login" replace />;

    const onDownload = async (inv) => {
        setDownloading(inv.id);
        try {
            const res = await axios.get(
                `${API}/client/invoices/${inv.id}/pdf`,
                { headers, responseType: "blob" },
            );
            const url = URL.createObjectURL(
                new Blob([res.data], { type: "application/pdf" }),
            );
            const a = document.createElement("a");
            a.href = url;
            a.download = `${inv.number}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error("Could not download PDF");
        } finally {
            setDownloading(null);
        }
    };

    return (
        <PortalShell>
            <div className="mb-10">
                <div className="text-[10px] uppercase tracking-[0.25em] text-ember mb-3">
                    Billing
                </div>
                <h1
                    data-testid="invoices-heading"
                    className="font-display text-bone text-5xl tracking-tightest leading-[1]"
                >
                    Invoices
                </h1>
                <p className="mt-3 text-bone-300 text-sm max-w-[60ch] inline-flex items-start gap-2">
                    <Banknote className="w-4 h-4 mt-0.5 text-ember shrink-0" />
                    Payments are made by bank transfer. Download the invoice
                    PDF — full bank details and your reference number are on
                    page one.
                </p>
            </div>

            {busy ? (
                <div className="flex items-center gap-3 text-bone-500 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading invoices…
                </div>
            ) : invoices.length === 0 ? (
                <div className="border border-white/10 bg-ink-800/40 p-12 text-center">
                    <FileText className="w-8 h-8 text-bone-500 mx-auto mb-4" />
                    <p className="text-bone-300">
                        No invoices yet. They'll appear here when issued.
                    </p>
                </div>
            ) : (
                <ul className="border-t border-white/10">
                    {invoices.map((inv) => (
                        <li
                            key={inv.id}
                            data-testid={`invoice-${inv.id}`}
                            className="border-b border-white/10 py-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center"
                        >
                            <div className="md:col-span-2">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-bone-500">
                                    Number
                                </div>
                                <div className="font-display text-bone tracking-tight">
                                    {inv.number}
                                </div>
                            </div>
                            <div className="md:col-span-4">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-bone-500">
                                    Title
                                </div>
                                <div className="font-display text-bone text-lg tracking-tight">
                                    {inv.title}
                                </div>
                            </div>
                            <div className="md:col-span-2">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-bone-500">
                                    Amount
                                </div>
                                <div className="font-display text-bone text-xl tracking-tight">
                                    {inv.amount.toFixed(2)}{" "}
                                    <span className="text-bone-300 text-xs uppercase">
                                        {inv.currency}
                                    </span>
                                </div>
                            </div>
                            <div className="md:col-span-1">
                                <span
                                    className={`text-[10px] uppercase tracking-[0.2em] border px-2 py-1 ${
                                        STATUS_BADGE[inv.status] ||
                                        "border-white/15 text-bone-300"
                                    }`}
                                >
                                    {inv.status}
                                </span>
                            </div>
                            <div className="md:col-span-3 md:justify-self-end flex items-center gap-2">
                                {inv.status === "paid" && (
                                    <span className="inline-flex items-center gap-2 text-emerald-400 text-xs uppercase tracking-[0.2em]">
                                        <CheckCircle2 className="w-4 h-4" />
                                        Paid
                                    </span>
                                )}
                                <button
                                    onClick={() => onDownload(inv)}
                                    disabled={downloading === inv.id}
                                    data-testid={`download-${inv.id}`}
                                    className="btn-ember inline-flex items-center gap-2 h-10 px-4 text-xs uppercase tracking-[0.2em] disabled:opacity-60"
                                >
                                    {downloading === inv.id ? (
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    ) : (
                                        <Download className="w-3.5 h-3.5" />
                                    )}
                                    PDF
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </PortalShell>
    );
}
